import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import MissingItemsList from '../components/customerPortal/MissingItemsList';
import AvailableForms from '../components/customerPortal/AvailableForms';
import MySubmissions from '../components/customerPortal/MySubmissions';
import CustomerDocuments from '../components/customerPortal/CustomerDocuments';
import { missingItemService } from '../services/missingItemService';
import {
  UserCircleIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  FolderIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const CustomerPortal = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'submissions' | 'missing-items' | 'documents'>('overview');
  const [missingItemsCount, setMissingItemsCount] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (user?.customerId) {
      loadMissingItemsCount();
    }
  }, [user]);

  const loadMissingItemsCount = async () => {
    if (!user?.customerId) return;
    try {
      const count = await missingItemService.getPendingCount(user.customerId);
      setMissingItemsCount(count);
    } catch (error) {
      console.error('Error loading missing items count:', error);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Main content area with sidebar */}
      <div className="flex w-full overflow-hidden">
        {/* Sidebar - Desktop */}
        <aside className="hidden xl:flex xl:flex-col xl:w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <UserCircleIcon className="h-8 w-8 text-primary-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('customerPortal.title')}
                </h2>
              </div>
            </div>
          </div>
          
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'overview'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'overview' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <ClipboardDocumentListIcon className="h-4 w-4" />
              <span>{t('customerPortal.tabs.overview')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('projects')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'projects'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'projects' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <ClipboardDocumentListIcon className="h-4 w-4" />
              <span>{t('customerPortal.tabs.projects')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('submissions')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'submissions'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'submissions' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <DocumentTextIcon className="h-4 w-4" />
              <span>{t('customerPortal.tabs.submissions')}</span>
            </button>
            
            <button
              onClick={() => setActiveTab('missing-items')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'missing-items'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'missing-items' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>{t('customerPortal.tabs.missingItems')}</span>
              </div>
              {missingItemsCount > 0 && (
                <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {missingItemsCount}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab('documents')}
              className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                activeTab === 'documents'
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {activeTab === 'documents' && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
              )}
              <FolderIcon className="h-4 w-4" />
              <span>{t('customerPortal.tabs.documents')}</span>
            </button>
          </nav>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && (
          <div className="xl:hidden fixed inset-0 z-40 flex">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsSidebarOpen(false)} />
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white dark:bg-gray-800">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                >
                  <XMarkIcon className="h-6 w-6 text-white" />
                </button>
              </div>
              
              {/* Mobile Sidebar Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <UserCircleIcon className="h-8 w-8 text-primary-600" />
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('customerPortal.title')}
                    </h2>
                  </div>
                </div>
              </div>
              
              <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                {/* Same buttons as desktop sidebar */}
                <button
                  onClick={() => { setActiveTab('overview'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'overview'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'overview' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                  <span>{t('customerPortal.tabs.overview')}</span>
                </button>
                
                <button
                  onClick={() => { setActiveTab('projects'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'projects'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'projects' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <ClipboardDocumentListIcon className="h-4 w-4" />
                  <span>{t('customerPortal.tabs.projects')}</span>
                </button>
                
                <button
                  onClick={() => { setActiveTab('submissions'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'submissions'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'submissions' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <DocumentTextIcon className="h-4 w-4" />
                  <span>{t('customerPortal.tabs.submissions')}</span>
                </button>
                
                <button
                  onClick={() => { setActiveTab('missing-items'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'missing-items'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'missing-items' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    <span>{t('customerPortal.tabs.missingItems')}</span>
                  </div>
                  {missingItemsCount > 0 && (
                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                      {missingItemsCount}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => { setActiveTab('documents'); setIsSidebarOpen(false); }}
                  className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all relative ${
                    activeTab === 'documents'
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {activeTab === 'documents' && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 rounded-r-full" />
                  )}
                  <FolderIcon className="h-4 w-4" />
                  <span>{t('customerPortal.tabs.documents')}</span>
                </button>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto relative">
          {/* Mobile Sidebar Indicator - Színes csík bal oldalon */}
          <div className={`
            xl:hidden fixed left-0 top-1/2 -translate-y-1/2 z-20
            w-3 h-24 bg-gradient-to-b from-primary-500 to-blue-500 rounded-r-lg shadow-lg
            transition-all duration-300 cursor-pointer
            ${isSidebarOpen ? 'opacity-0 -translate-x-4' : 'opacity-80 hover:opacity-100 hover:w-4 hover:shadow-xl'}
          `}
            onClick={() => setIsSidebarOpen(true)}
            title="Menü megnyitása"
          />
          
          <div className="p-3 sm:p-4 md:p-6">
            <div className="space-y-4 sm:space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Stat Cards */}
            <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{t('customerPortal.overview.activeProjects')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mt-0.5 sm:mt-1">0</p>
                </div>
                <ClipboardDocumentListIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500 flex-shrink-0" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{t('customerPortal.overview.submittedRequests')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mt-0.5 sm:mt-1">0</p>
                </div>
                <DocumentTextIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 flex-shrink-0" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{t('customerPortal.overview.missingDocuments')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400 mt-0.5 sm:mt-1">{missingItemsCount}</p>
                </div>
                <ExclamationTriangleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-500 flex-shrink-0" />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-3 sm:p-4 md:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">{t('customerPortal.overview.closedProjects')}</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mt-0.5 sm:mt-1">0</p>
                </div>
                <CheckCircleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500 flex-shrink-0" />
              </div>
            </div>
          </div>
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <div className="text-center py-8 sm:py-12">
              <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('customerPortal.projects.noProjects')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('customerPortal.projects.description')}
              </p>
            </div>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Available Forms Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
                {t('customerPortal.submissions.availableForms')}
              </h3>
              <AvailableForms />
            </div>

            {/* My Submissions Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">
                {t('customerPortal.submissions.mySubmissions')}
              </h3>
              <MySubmissions />
            </div>
          </div>
        )}

        {/* Missing Items Tab */}
        {activeTab === 'missing-items' && (
          <div className="space-y-3 sm:space-y-4">
            {/* Warning Message - csak ha van hiányzó tétel */}
            {missingItemsCount > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 sm:p-6">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                      {t('customerPortal.missingItems.warning')}
                    </h3>
                    <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                      <p>
                        {t('customerPortal.missingItems.warningMessage')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Missing Items List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 sm:p-6">
              <MissingItemsList customerId={user?.customerId} isCustomerView={true} />
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <CustomerDocuments />
          </div>
        )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CustomerPortal;
